import { createRouter, createWebHistory } from 'vue-router'

import { errorHandler } from '@/composables/errorHandler'
import { hasPermission } from '@/composables/usePermissionHandler'
import { appConfig } from '@/config.js'
import { t } from '@/i18n'
import { notificationService } from '@/services/notificationService'
import { useCommonStore, useUserStore } from '@/stores'

import AdministrationRoutes from './routes/administration'
import CalculationsRoutes from './routes/calculations'
import CampaignsRoutes from './routes/campaigns'
import ConfigurationsRoutes from './routes/configurations'
import CustomersRoutes from './routes/customers'
import CooperationJobsRoutes from './routes/cooperationJobs'
import CuttingJobsRoutes from './routes/cuttingJobs'
import Dashboard from './routes/dashboard'
import DocumentsRoutes from './routes/documents'
import JobsRoutes from './routes/jobs'
import JobsForProofRoutes from './routes/jobsForProof'
import MaintenanceRoutes from './routes/maintenance'
import NotesRoutes from './routes/notes'
import OrdersRoutes from './routes/orders'
import PackagePrototypeJobsRoutes from './routes/packagePrototypeJobs'
import PrepressRoutes from './routes/prepress'
import PurchasesRoutes from './routes/purchases'
import QualityRoutes from './routes/quality'
import ReportingAndAnalysisRoutes from './routes/reportingAndAnalysis'
import ReportsRoutes from './routes/reports'
import ShipmentsRoutes from './routes/shipments'
import UserLogin from './routes/userLogin'
import WikiRoutes from './routes/wiki'

// Combined routes
const routes = [
    ...UserLogin,
    ...Dashboard,
    ...NotesRoutes,
    ...CustomersRoutes,
    ...CampaignsRoutes,
    ...CalculationsRoutes,
    ...OrdersRoutes,
    ...JobsRoutes,
    ...JobsForProofRoutes,
    ...PackagePrototypeJobsRoutes,
    ...CuttingJobsRoutes,
    ...CooperationJobsRoutes,
    ...ReportsRoutes,
    ...MaintenanceRoutes,
    ...DocumentsRoutes,
    ...ShipmentsRoutes,
    ...QualityRoutes,
    ...PurchasesRoutes,
    ...ConfigurationsRoutes,
    ...AdministrationRoutes,
    ...ReportingAndAnalysisRoutes,
    ...PrepressRoutes,
    ...WikiRoutes
]

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
    scrollBehavior() {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve({ left: 0, top: 0 })
            }, 400)
        })
    }
})

// Initialize stores once
let userStore
let commonStore

// Navigation Guards
router.beforeEach((to, from, next) => {
    // Initialize stores lazily
    if (!userStore) userStore = useUserStore()
    if (!commonStore) commonStore = useCommonStore()

    commonStore.toggleMainLoader(true)

    const titleKey = to.meta.title
    if (titleKey) {
        const translatedTitle = t(titleKey)
        document.title = `${appConfig.common.appName} - ${translatedTitle}`
    } else {
        document.title = appConfig.common.appName
    }

    if (to.matched.some((record) => record.meta.requiresAuth)) {
        // Jeśli strona wymaga autoryzacji przekieruj na formularz logowania
        if (!userStore.loggedIn) {
            commonStore.toggleMainLoader(false)
            next({
                name: 'userLogin'
            })
        } else {
            if (to.meta.permission) {
                if (hasPermission(to.meta.permission)) {
                    next()
                } else {
                    notificationService.error(t('common.router.noPermission'))
                    commonStore.toggleMainLoader(false)
                    // Redirect to the dashboard
                    next({
                        name: 'dashboard',
                        replace: true
                    })
                }
            } else {
                next()
            }
        }
    } else if (to.matched.some((record) => record.meta.requiresVisitor)) {
        if (userStore.loggedIn) {
            commonStore.toggleMainLoader(false)
            next({
                name: 'dashboard'
            })
        } else {
            next()
        }
    } else if (!to.matched.length) {
        // Redirect to a safe default route
        commonStore.toggleMainLoader(false)
        if (userStore.loggedIn) {
            next({ name: 'dashboard', replace: true })
        } else {
            next({ name: 'userLogin', replace: true })
        }
    } else {
        next()
    }
})

router.afterEach(() => {
    if (!commonStore) commonStore = useCommonStore()
    commonStore.toggleMainLoader(false)
    commonStore.toggleCircularLoader(false)

    // Check for service worker updates on every navigation
    if (import.meta.env.PROD && 'serviceWorker' in navigator) {
        navigator.serviceWorker.ready
            .then((reg) => reg.update())
            .catch(() => {
                // Silently fail - update check is not critical
            })
    }
})

router.onError((error) => {
    if (!commonStore) commonStore = useCommonStore()

    errorHandler(error)
    // Don't call toggleMainLoader here since errorHandler might do it
    // and afterEach will also be called
})

export default router
